import { useLang } from '@/i18n';
import { subscribeCookie } from '@/utils/cookies/subscribeCookie';
import { useGlobal } from '@/utils/useGlobal';
import EcoSpace from 'eco-design/base/eco-space';
import EditorLink from 'lowcode/components/EditorLink';
import { baseUrl } from 'lowcode/utils/service/baseUrl';
import { fetchPost } from 'lowcode/utils/service/FetchClient';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import css from './index.module.scss';
import Modal from './Modal';

const MoreIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="7" height="10" viewBox="0 0 7 10" fill="none">
        <path d="M1.5 9L5.5 5L1.5 1" stroke="#3F68E0" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const contactListIds = {
    eu: '1585617461',
    uk: '1372111406',
    de: '356112763',
    fr: '1615613479',
    it: '1004671813',
    es: '1212554384',
};

interface Props {
    endTime: number;
}

const TheComp: React.FC<Props> = ({ endTime }) => {
    const { locale = 'us' } = useRouter();
    const { getSystemTime, useRouter: getRouter } = useGlobal();
    const router = getRouter();

    const { t } = useLang('rapidseries.benefits');
    const [visible, setVisible] = useState(false);
    const [days, setDays] = useState('16');
    const [hours, setHours] = useState('23');
    const [minutes, setMinutes] = useState('59');
    const [seconds, setSeconds] = useState('59');
    const [input, setInput] = useState<string>('');
    const [tip, setTip] = useState<string>('');
    const [isAgreed, setIsAgreed] = useState(false);
    const timerLabels = t('timer').split(' ');

    const benefitsData = [
        {
            title: t('item1_title'),
            content: t('item1_content'),
            date: t('item1_date'),
            image: (() => {
                const img = {
                    en: 'https://websiteoss.ecoflow.com/upload/image/1750400777411/EN.png',
                    es: 'https://websiteoss.ecoflow.com/upload/image/1750400777228/ES.png',
                    it: 'https://websiteoss.ecoflow.com/upload/image/1750400776858/IT.png',
                    fr: 'https://websiteoss.ecoflow.com/upload/image/1750400777061/FR.png',
                    de: 'https://websiteoss.ecoflow.com/upload/image/1750400776666/DE.png',
                };
                return img[locale as 'en'] || img['en'];
            })(),
        },
        {
            title: t('item2_title'),
            content: t('item2_content'),
            image: 'https://websiteoss.ecoflow.com/upload/image/1750068608934/img2.png',
        },
        {
            title: t('item3_title'),
            content: t('item3_content'),
            image: (() => {
                const img = {
                    en: 'https://websiteoss.ecoflow.com/upload/image/1750400870271/EN.png',
                    de: 'https://websiteoss.ecoflow.com/upload/image/1750400869750/DE.png',
                    fr: 'https://websiteoss.ecoflow.com/upload/image/1750400870445/FR.png',
                    it: 'https://websiteoss.ecoflow.com/upload/image/1750400870642/IT.png',
                    es: 'https://websiteoss.ecoflow.com/upload/image/1750400870118/ES.png',
                };
                return img[locale as 'en'] || img['en'];
            })(),
            cta: {
                text: t('item3_cta'),
                href: `https://${locale}.ecoflow.com/collections/power-bank?powerbank-trade-in=true`,
            },
        },
    ];

    useEffect(() => {
        const endDate = new Date(endTime);
        const timer = setInterval(() => {
            const now = new Date();
            const diff = endDate.getTime() - now.getTime();

            const d = Math.floor(diff / (1000 * 60 * 60 * 24));
            const h = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
            const s = Math.floor((diff % (1000 * 60)) / 1000);

            setDays(String(d).padStart(2, '0'));
            setHours(String(h).padStart(2, '0'));
            setMinutes(String(m).padStart(2, '0'));
            setSeconds(String(s).padStart(2, '0'));
        }, 1000);

        return () => clearInterval(timer);
    }, []);

    const handleFocus = () => {
        if (tip) setTip('');
    };

    const handleBlur = () => {
        const email = input.trim();
        if (!email) {
            setTip(t('subscribe_empty'));
            return;
        }
        const regex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
        if (!regex.test(email)) {
            setTip(t('subscribe_error'));
            return;
        }
        if (!isAgreed) {
            setTip(t('subscribe_agreement'));
            return;
        }
        return true;
    };
    const handleSubmit = async () => {
        if (!handleBlur()) return;

        const email = input.trim();
        const date = new Date();
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const dateString = `${year}-${month}-${day}`;

        const EU_Fields = [
            { type: 'single', id: '31', value: 1, overwrite: true },
            { type: 'multiple', id: '7596', value: [49], overwrite: false },
            { type: 'multiple', id: '9690', value: [12], overwrite: false },
        ];
        const configs = {
            eu: {
                contactListId: [contactListIds.eu],
                fields: [{ type: 'single', id: '4427', value: 1, overwrite: true }, ...EU_Fields],
            },
            uk: {
                contactListId: [contactListIds.uk],
                fields: [{ type: 'single', id: '4430', value: 1, overwrite: true }, ...EU_Fields],
            },
            de: {
                contactListId: [contactListIds.de],
                fields: [{ type: 'single', id: '4425', value: 1, overwrite: true }, ...EU_Fields],
            },
            fr: {
                contactListId: [contactListIds.fr],
                fields: [{ type: 'single', id: '4428', value: 1, overwrite: true }, ...EU_Fields],
            },
            it: {
                contactListId: [contactListIds.it],
                fields: [{ type: 'single', id: '4429', value: 1, overwrite: true }, ...EU_Fields],
            },
            es: {
                contactListId: [contactListIds.es],
                fields: [{ type: 'single', id: '4426', value: 1, overwrite: true }, ...EU_Fields],
            },
        };

        const { contactListId, fields } = configs[locale as keyof typeof configs] || configs['eu'];
        const res = await fetchPost(
            `${baseUrl(locale)}/subscribe/new`,
            locale,
            {
                contactListId: contactListId[0],
                email,
                fields,
                source: 'WEB_NEW_PRODUCT_SUB',
                otherContactListIds: contactListId.slice(1),
                eventTracking: subscribeCookie({
                    sector_name: t('title'),
                    entry_position: 5,
                }),
            },
            {
                headers: {
                    // apiKey: '527760f6df93c0d6e98609fa8e8a1f4a',
                },
            },
        );
        if (res?.code === '0') {
            setTip(t('subscribe_success'));
            setInput('');
            setTimeout(() => {
                setTip('');
            }, 3000);
        } else {
            setTip(res?.message);
            console.error(res?.message);
        }
    };

    const handleOpenModal = () => {
        setVisible(true);
    };
    const handleCloseModal = () => {
        setVisible(false);
    };

    let systemTime = getSystemTime();
    const { _timer } = router.query;
    if (_timer) {
        systemTime = new Date(_timer).getTime();
    }
    if (systemTime >= endTime) return null;
    return (
        <div className={css.thecomp}>
            <EcoSpace />
            <div className="container">
                <h2>{t('title')}</h2>
                <div className="timer">
                    <div className="timer-item">
                        <span className="number">{days}</span>
                        <span className="label">{timerLabels[0]}</span>
                    </div>
                    <div className="timer-item">
                        <span className="number">{hours}</span>
                        <span className="label">{timerLabels[1]}</span>
                    </div>
                    <div className="timer-item">
                        <span className="number">{minutes}</span>
                        <span className="label">{timerLabels[2]}</span>
                    </div>
                    <div className="timer-item">
                        <span className="number">{seconds}</span>
                        <span className="label">{timerLabels[3]}</span>
                    </div>
                </div>
                <div className="email-form-container">
                    <div className="email-form">
                        <input
                            type="email"
                            placeholder={t('email_placeholder')}
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                        />
                        <button onClick={handleSubmit} disabled={!isAgreed}>
                            {t('submit')}
                        </button>
                    </div>
                    <div className="agreement-box">
                        <input type="checkbox" id="agreement" checked={isAgreed} onChange={(e) => setIsAgreed(e.target.checked)} />
                        <label className="agreement">
                            {t.rich('agreement', {
                                link1: (tx: string) => (
                                    <EditorLink href={`https://${locale}.ecoflow.com/policies/terms-of-service`} className="agreement-link" target="_blank">
                                        {tx}
                                    </EditorLink>
                                ),
                                link2: (tx: string) => (
                                    <EditorLink href={`https://${locale}.ecoflow.com/policies/privacy-policy`} className="agreement-link" target="_blank">
                                        {tx}
                                    </EditorLink>
                                ),
                            })}
                        </label>
                    </div>
                    {tip && <p className={`tip${t('subscribe_success') === tip ? ' success' : ''}`}>{tip}</p>}
                </div>

                <div className="benefits-grid">
                    {benefitsData.map((item, index) => (
                        <div className="benefit-item" key={index}>
                            <div className="benefit-image">
                                <img src={item.image} alt={item.title} />
                            </div>
                            <div className="item-content">
                                <h3>{item.title}</h3>
                                <p>{item.content}</p>
                                {item.date && <span className="date">{item.date}</span>}
                                {item.cta && (
                                    <EditorLink href={item.cta.href} className="cta-link" target="_blank">
                                        {item.cta.text} <MoreIcon />
                                    </EditorLink>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
                <div className="diglog-text" onClick={handleOpenModal}>
                    {t('tip')}
                </div>
                {visible && createPortal(<Modal callback={handleCloseModal} />, document.body)}
            </div>
        </div>
    );
};

export default TheComp;
